import React, { useEffect, useState } from 'react'
import { Upload, message } from 'antd'
import type { GetProp, UploadFile, UploadProps } from 'antd'
import { RcFile } from 'antd/es/upload'
import { v4 as uuidv4 } from 'uuid'
import { useSelector } from 'react-redux'
import { uploadFile, deleteFile } from '../../Utility/storage/storage'

import { UploadOutlined, InboxOutlined, PlusOutlined } from '@ant-design/icons'

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0]

const UploadDocument = ({
  value,
  onChange,
  accept,
  maxCount,
  description,
  name,
  allowDelete = true
}: any) => {
  const [fileList, setFileList] = useState<UploadFile[]>(value || [])
  const userRedux = useSelector((state: any) => state.user)

  useEffect(() => {
    setFileList(value || [])
  }, [value])

  const onUploadChange: UploadProps['onChange'] = ({
    fileList: newFileList
  }) => {
    setFileList(newFileList)
  }

  const onPreview = async (file: UploadFile) => {
    let src = file.url as string
    if (!src) {
      src = await new Promise((resolve) => {
        const reader = new FileReader()
        reader.readAsDataURL(file.originFileObj as FileType)
        reader.onload = () => resolve(reader.result as string)
      })
    }
    const pdfWindow = window.open('')
    if (pdfWindow) {
      if (accept !== 'application/pdf') {
        pdfWindow.document.write(
          `<img src="${src}" style="max-width: 100%;" />`
        )
      } else {
        pdfWindow.document.write(
          `<iframe width='100%' height='100%' style='border: none;' src='${src}'></iframe>`
        )
      }
    }
  }

  const onRemove = (file: UploadFile) => {
    if (!allowDelete) return false

    const restaurantId = userRedux.vendorInfo?.restaurantId || 'unknown'
    const path = `${restaurantId}/restaurantDocuments/${name}/${file.name}`
    deleteFile(path)
      .then(() => {
        const updatedValue = (value || []).filter(
          (doc: any) => doc.name !== file.name
        )
        setFileList(updatedValue)
        onChange(updatedValue)
      })
      .catch((error: any) => {
        console.log(error)
        const updatedFileList = fileList.filter(
          (doc: any) => doc.uid !== file.uid
        )
        setFileList(updatedFileList)
        onChange(updatedFileList)
      })
  }

  const beforeUpload = (file: RcFile) => {
    if (accept) {
      const allowedTypes = accept.split(',').map((t: string) => t.trim())
      const isAllowedType = allowedTypes.some((type: string) => {
        if (type.endsWith('/*')) {
          return file.type.startsWith(type.replace('/*', ''))
        }
        if (type.startsWith('.')) {
          return file.name.toLowerCase().endsWith(type.toLowerCase())
        }
        return file.type === type
      })

      if (!isAllowedType) {
        message.error(`Invalid file format. Expected: ${accept}`)
        return Upload.LIST_IGNORE
      }
    }

    const isLessThan2M = file.size / 1024 / 1024 < 2
    if (!isLessThan2M) {
      message.error(`${file.name} must be smaller than 2MB!`)
      return Upload.LIST_IGNORE
    }
    return true
  }

  const uploadDoc = async (file: RcFile) => {
    const restaurantId = userRedux.vendorInfo?.restaurantId || 'unknown'
    const path = `${restaurantId}/restaurantDocuments/${name}/${file.name}`
    try {
      const url = await uploadFile(path, file)
      const fileObject = {
        uid: uuidv4(),
        name: file.name,
        url: url,
        status: 'done'
      }
      const updatedDocs = value ? [...value, fileObject] : [fileObject]
      onChange(updatedDocs)
    } catch (error: any) {
      console.log('upload error', error)
    }
    return ''
  }

  const customRequest = async (options: any) => {
    const { file, onSuccess, onError } = options
    try {
      await uploadDoc(file as RcFile)
      onSuccess('ok')
    } catch (err) {
      onError(err)
    }
  }

  const isImage = accept.includes('image')

  if (isImage) {
    const uploadButton = (
      <div>
        <PlusOutlined />
        <div style={{ marginTop: 8 }}>Upload</div>
        {description && (
          <div style={{ fontSize: '10px', color: '#8c8c8c' }}>
            {description}
          </div>
        )}
      </div>
    )

    return (
      <Upload
        customRequest={customRequest}
        beforeUpload={beforeUpload}
        listType='picture-card'
        fileList={fileList}
        onChange={onUploadChange}
        onPreview={onPreview}
        onRemove={onRemove}
        accept={accept}
        maxCount={maxCount}
        multiple={maxCount > 1}
        showUploadList={{ showRemoveIcon: allowDelete }}
      >
        {fileList.length < maxCount && uploadButton}
      </Upload>
    )
  }

  // Only swap to the list view when files are fully uploaded.
  // Swapping immediately on selection unmounts Upload.Dragger and aborts the upload!
  const doneCount = fileList.filter((f) => f.status === 'done' || f.url).length
  if (doneCount >= maxCount) {
    return (
      <Upload
        listType='picture'
        fileList={fileList}
        onChange={onUploadChange}
        onPreview={onPreview}
        onRemove={onRemove}
        accept={accept}
        maxCount={maxCount}
        multiple={maxCount > 1}
        showUploadList={{ showRemoveIcon: allowDelete }}
      />
    )
  }

  return (
    <Upload.Dragger
      customRequest={customRequest}
      beforeUpload={beforeUpload}
      listType='picture'
      fileList={fileList}
      onChange={onUploadChange}
      onPreview={onPreview}
      onRemove={onRemove}
      accept={accept}
      maxCount={maxCount}
      multiple={maxCount > 1}
      showUploadList={{ showRemoveIcon: allowDelete }}
    >
      <p className='ant-upload-drag-icon'>
        <InboxOutlined />
      </p>
      <p className='ant-upload-text'>
        Click or drag file to this area to upload
      </p>
      {description && <p className='ant-upload-hint'>{description}</p>}
    </Upload.Dragger>
  )
}

export default UploadDocument
